import { useState } from 'react';
import ColumnVisibility from './column-visibility.component';
import type { DataGridColumnSettings } from './data-grid-settings.interface';
import type { Signal } from '@preact/signals-react';

export interface DataGridSettingsMenuItem {
  label?: string;
  key: string;
  isSeparator?: boolean;
  subMenuItems?: Array<SubMenuItem>;
}

export interface SubMenuItem {
  label?: string;
  key: string;
  isSeparator?: boolean;
}

export interface DataGridSettingsSubMenuProps {
  items: Array<DataGridSettingsMenuItem>;
  onItemClick: (menuKey: string) => void;
  subMenuVisibility: boolean;
}

const SubmenuItems = ({ items, onItemClick, subMenuVisibility }: DataGridSettingsSubMenuProps) => {
  return (
    <div
      style={{ left: '-50px' }}
      className={`odin-inset-y-15 odin-absolute -odin-top-2 odin-z-10 ${
        !subMenuVisibility && 'odin-hidden'
      } odin-origin-top-left odin-rounded-bl-md odin-rounded-tl-md odin-bg-mono-1 odin-py-2 odin-text-sm odin-shadow-md odin-drop-shadow`}
    >
      {items.map((item) => {
        return (
          <div
            key={item.key}
            className={`odin-cursor-pointer odin-leading-6 ${
              item.isSeparator ? 'odin-py-1' : 'odin-px-3 odin-py-px hover:odin-bg-primary-4'
            }`}
            onClick={() => onItemClick(item.key)}
          >
            {!item.isSeparator ? item.label : <hr className="odin-border-gray-3" />}
          </div>
        );
      })}
    </div>
  );
};

export interface DataGridSettingsMenuProps {
  items: Array<DataGridSettingsMenuItem>;
  onItemClick: (menuKey: string) => void;
  columns: Signal<Array<DataGridColumnSettings>>;
  onColumnsVisibilityChanged: (columns: Array<DataGridColumnSettings>) => void;
  showGridColumnsOnSettingsMenu: boolean;
  setOverlayVisible?: (value: boolean) => void;
  showGridMenu?: boolean;
}

export default function DataGridSettingsMenu({
  items,
  onItemClick,
  columns,
  onColumnsVisibilityChanged,
  showGridColumnsOnSettingsMenu,
  setOverlayVisible,
  showGridMenu = true,
}: DataGridSettingsMenuProps) {
  const [subMenuVisibility, setSubMenuVisibility] = useState(false);
  return (
    <div className="odin-rounded-bl-md odin-rounded-tl-md odin-bg-mono-1 odin-py-2 odin-text-sm odin-shadow-md">
      {showGridMenu &&
        items.map((item) => (
          <div
            key={item.key}
            className={`hover:odin-bg-gray-100 odin-relative odin-cursor-pointer odin-rounded-sm odin-px-3 odin-py-1 odin-leading-6 ${
              item.isSeparator ? 'odin-py-1' : 'odin-px-3 odin-py-px hover:odin-bg-primary-4'
            }`}
            onClick={() => onItemClick(item.key)}
            onMouseEnter={() => item?.subMenuItems && setSubMenuVisibility(true)}
            onMouseLeave={() => item?.subMenuItems && setSubMenuVisibility(false)}
          >
            {!item.isSeparator ? item.label : <hr className="odin-border-gray-3" />}
            {item?.subMenuItems && (
              <SubmenuItems
                items={item?.subMenuItems}
                onItemClick={onItemClick}
                subMenuVisibility={subMenuVisibility}
              />
            )}
          </div>
        ))}
      {showGridColumnsOnSettingsMenu && (
        <div className="odin-px-2">
          <ColumnVisibility
            columns={columns}
            onColumnsVisibilityChanged={onColumnsVisibilityChanged}
            setOverlayVisible={setOverlayVisible}
          />
        </div>
      )}
    </div>
  );
}
